import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard, 
  FileText, 
  AlertTriangle, 
  History,
  LogOut,
  MapPin,
  Monitor,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Users,
  Building,
  Store,
  Gift,
  BarChart3
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import { Label } from "../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSites() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sites, setSites] = useState([]);
  const [kiosks, setKiosks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSiteDialog, setShowSiteDialog] = useState(false);
  const [showKioskDialog, setShowKioskDialog] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [editingKiosk, setEditingKiosk] = useState(null);
  const [siteForm, setSiteForm] = useState({ name: "", address: "", region: "" });
  const [kioskForm, setKioskForm] = useState({ site_id: "", name: "", location_description: "" });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      fetchData();
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sitesRes, kiosksRes] = await Promise.all([
        axios.get(`${API}/sites?include_inactive=true`, { withCredentials: true }),
        axios.get(`${API}/kiosks?include_inactive=true`, { withCredentials: true })
      ]);
      setSites(sitesRes.data);
      setKiosks(kiosksRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSite = async () => {
    try {
      if (editingSite) {
        await axios.patch(`${API}/sites/${editingSite.site_id}`, siteForm, { withCredentials: true });
        toast.success("Site updated");
      } else {
        await axios.post(`${API}/sites`, siteForm, { withCredentials: true });
        toast.success("Site created");
      }
      setShowSiteDialog(false);
      setSiteForm({ name: "", address: "", region: "" });
      setEditingSite(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to save site");
    }
  };

  const handleSaveKiosk = async () => {
    try {
      if (editingKiosk) {
        await axios.patch(`${API}/kiosks/${editingKiosk.kiosk_id}`, kioskForm, { withCredentials: true });
        toast.success("Kiosk updated");
      } else {
        await axios.post(`${API}/kiosks`, kioskForm, { withCredentials: true });
        toast.success("Kiosk created");
      }
      setShowKioskDialog(false);
      setKioskForm({ site_id: "", name: "", location_description: "" });
      setEditingKiosk(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to save kiosk");
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm("Are you sure you want to deactivate this site?")) return;
    try {
      await axios.delete(`${API}/sites/${siteId}`, { withCredentials: true });
      toast.success("Site deactivated");
      fetchData();
    } catch (error) {
      toast.error("Failed to deactivate site");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {}
    navigate("/admin/login");
  };

  const openEditSite = (site) => {
    setEditingSite(site);
    setSiteForm({ name: site.name, address: site.address || "", region: site.region || "" });
    setShowSiteDialog(true);
  };

  const openEditKiosk = (kiosk) => {
    setEditingKiosk(kiosk);
    setKioskForm({ site_id: kiosk.site_id, name: kiosk.name, location_description: kiosk.location_description || "" });
    setShowKioskDialog(true);
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.site_id === siteId);
    return site ? site.name : siteId;
  };

  return (
    <div className="admin-container min-h-screen" data-testid="admin-sites">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <FileText className="w-5 h-5" />
            <span>Registrations</span>
          </Link>
          <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
            <Building className="w-5 h-5" />
            <span>Sites & Kiosks</span>
          </Link>
          <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Store className="w-5 h-5" />
            <span>Retailers</span>
          </Link>
          <Link to="/admin/rewards" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Gift className="w-5 h-5" />
            <span>Rewards</span>
          </Link>
          <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <BarChart3 className="w-5 h-5" />
            <span>Analytics</span>
          </Link>
          <Link to="/admin/ocr-exceptions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <AlertTriangle className="w-5 h-5" />
            <span>OCR Exceptions</span>
          </Link>
          <Link to="/admin/audit-log" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <History className="w-5 h-5" />
            <span>Audit Log</span>
          </Link>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full">
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Sites & Kiosks
          </h1>
          <p className="text-slate-400">Manage parking sites and kiosk deployments</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Sites Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-400" />
                  Sites ({sites.length})
                </h2>
                <Dialog open={showSiteDialog} onOpenChange={setShowSiteDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { setEditingSite(null); setSiteForm({ name: "", address: "", region: "" }); }} className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Site
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900 border-slate-700">
                    <DialogHeader>
                      <DialogTitle className="text-white">{editingSite ? "Edit Site" : "Add New Site"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label className="text-slate-300">Site Name *</Label>
                        <Input value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} placeholder="e.g. Town Centre Car Park" className="bg-slate-800 border-slate-700 text-white" />
                      </div>
                      <div>
                        <Label className="text-slate-300">Address</Label>
                        <Input value={siteForm.address} onChange={(e) => setSiteForm({ ...siteForm, address: e.target.value })} placeholder="Full address" className="bg-slate-800 border-slate-700 text-white" />
                      </div>
                      <div>
                        <Label className="text-slate-300">Region</Label>
                        <Input value={siteForm.region} onChange={(e) => setSiteForm({ ...siteForm, region: e.target.value })} placeholder="e.g. South Ayrshire" className="bg-slate-800 border-slate-700 text-white" />
                      </div>
                      <Button onClick={handleSaveSite} className="w-full bg-blue-600 hover:bg-blue-700" disabled={!siteForm.name}>
                        {editingSite ? "Update Site" : "Create Site"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="admin-card border-slate-800 overflow-hidden">
                <Table className="admin-table">
                  <TableHeader>
                    <TableRow className="border-slate-800 hover:bg-transparent">
                      <TableHead className="text-slate-400">Site ID</TableHead>
                      <TableHead className="text-slate-400">Name</TableHead>
                      <TableHead className="text-slate-400">Address</TableHead>
                      <TableHead className="text-slate-400">Region</TableHead>
                      <TableHead className="text-slate-400">Kiosks</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sites.map((site) => (
                      <TableRow key={site.site_id} className="border-slate-800/50 hover:bg-slate-800/30">
                        <TableCell className="font-mono text-slate-400 text-sm">{site.site_id}</TableCell>
                        <TableCell className="text-white font-medium">{site.name}</TableCell>
                        <TableCell className="text-slate-300">{site.address || "—"}</TableCell>
                        <TableCell className="text-slate-300">{site.region || "—"}</TableCell>
                        <TableCell className="text-slate-300">{kiosks.filter(k => k.site_id === site.site_id).length}</TableCell>
                        <TableCell>
                          <span className={`status-badge ${site.is_active ? 'status-active' : 'status-revoked'}`}>
                            {site.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditSite(site)} className="text-blue-400 hover:text-blue-300 hover:bg-slate-800">
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteSite(site.site_id)} className="text-red-400 hover:text-red-300 hover:bg-slate-800">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Kiosks Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-green-400" />
                  Kiosks ({kiosks.length})
                </h2>
                <Dialog open={showKioskDialog} onOpenChange={setShowKioskDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { setEditingKiosk(null); setKioskForm({ site_id: sites[0]?.site_id || "", name: "", location_description: "" }); }} className="bg-green-600 hover:bg-green-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Kiosk
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900 border-slate-700">
                    <DialogHeader>
                      <DialogTitle className="text-white">{editingKiosk ? "Edit Kiosk" : "Add New Kiosk"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label className="text-slate-300">Site *</Label>
                        <select value={kioskForm.site_id} onChange={(e) => setKioskForm({ ...kioskForm, site_id: e.target.value })} className="w-full h-10 px-3 bg-slate-800 border border-slate-700 text-white rounded-md">
                          {sites.filter(s => s.is_active).map(site => (
                            <option key={site.site_id} value={site.site_id}>{site.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Kiosk Name *</Label>
                        <Input value={kioskForm.name} onChange={(e) => setKioskForm({ ...kioskForm, name: e.target.value })} placeholder="e.g. Entrance Kiosk A" className="bg-slate-800 border-slate-700 text-white" />
                      </div>
                      <div>
                        <Label className="text-slate-300">Location Description</Label>
                        <Input value={kioskForm.location_description} onChange={(e) => setKioskForm({ ...kioskForm, location_description: e.target.value })} placeholder="e.g. Ground floor near main entrance" className="bg-slate-800 border-slate-700 text-white" />
                      </div>
                      <Button onClick={handleSaveKiosk} className="w-full bg-green-600 hover:bg-green-700" disabled={!kioskForm.name || !kioskForm.site_id}>
                        {editingKiosk ? "Update Kiosk" : "Create Kiosk"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="admin-card border-slate-800 overflow-hidden">
                <Table className="admin-table">
                  <TableHeader>
                    <TableRow className="border-slate-800 hover:bg-transparent">
                      <TableHead className="text-slate-400">Kiosk ID</TableHead>
                      <TableHead className="text-slate-400">Name</TableHead>
                      <TableHead className="text-slate-400">Site</TableHead>
                      <TableHead className="text-slate-400">Location</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {kiosks.map((kiosk) => (
                      <TableRow key={kiosk.kiosk_id} className="border-slate-800/50 hover:bg-slate-800/30">
                        <TableCell className="font-mono text-slate-400 text-sm">{kiosk.kiosk_id}</TableCell>
                        <TableCell className="text-white font-medium">{kiosk.name}</TableCell>
                        <TableCell className="text-slate-300">{getSiteName(kiosk.site_id)}</TableCell>
                        <TableCell className="text-slate-300">{kiosk.location_description || "—"}</TableCell>
                        <TableCell>
                          <span className={`status-badge ${kiosk.is_active ? 'status-active' : 'status-revoked'}`}>
                            {kiosk.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => openEditKiosk(kiosk)} className="text-blue-400 hover:text-blue-300 hover:bg-slate-800">
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
